import React, { Suspense, useCallback, useEffect } from 'react';
import type { IContent } from '@/@types/cms';
import { pageFactory } from './pages';
import { ErrorBoundary } from 'react-error-boundary';
import { componentFactory } from './components';
import { datadogRum } from '@/lib/DatadogService';
import ComponentLazyShimmer from './ComponentLazyShimmer';
import PageLoading from '@/routes/PageLoading';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GlobalErrorPage from '../components/pages/GlobalErrorPage';
import { selectPage } from '@/redux/selectors/pageSelectors';
import ContentArea from '@/components/cms/ContentArea';

interface IComponentFactoryProps {
  components?: Array<IContent>;
  componentsProps?: Record<string, unknown>;
}

export interface IComponentProps extends IContent {
  [key: string]: unknown;
  isFirstItem?: boolean;
  isLastItem?: boolean;
  index?: number;
  fallbackBlock?: IContent[];
}

const logMissingComponentError = (_type: string, componentType: 'Page' | 'Component') => {
  datadogRum.addAction(`Missing ${componentType} view: ${_type ?? 'UNDEFINED'}`);
};

export const Page: React.FC<IComponentProps> = React.memo(({ contentType, ...props }) => {
  const pageType = `${contentType?.slice(-1)[0]}`;
  const PageComponent = pageFactory(pageType);
  const navigate = useNavigate();
  const pages = useSelector(selectPage);

  const sanitizeToPath = useCallback((value: string): string => {
    try {
      const url = new URL(value);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return value.startsWith('/') ? value : `/${value}`;
    }
  }, []);

  useEffect(() => {
    if (!PageComponent) {
      logMissingComponentError(pageType, 'Page');
      if (window.location.pathname != '/') {
        window.location.assign('/');
      }
    }
  }, [PageComponent, pageType]);

  if (!PageComponent) {
    return null;
  }
  return (
    <ErrorBoundary
      fallback={
        pages?.pageLinks?.GlobalError ? null : (
          <GlobalErrorPage {...props} renderBackupContent={true} />
        )
      }
      onError={error => {
        datadogRum.addError(error);
        if (pages?.pageLinks?.GlobalError) navigate(sanitizeToPath(pages?.pageLinks?.GlobalError));
      }}
    >
      <Suspense fallback={<PageLoading show={true} />}>
        <PageComponent {...props} contentType={contentType} />
      </Suspense>
    </ErrorBoundary>
  );
});

const componentShimmerBlacklist = ['navigation', 'footer'];

export const Component: React.FC<IComponentProps> = React.memo(({ contentType, ...props }) => {
  const componentType = contentType?.slice(-1)[0] ?? '';
  const SingleComponent = componentFactory(`${componentType}`);
  if (!SingleComponent) logMissingComponentError(componentType, 'Component');
  const showFallback = !componentShimmerBlacklist.some(
    a => componentType?.toLowerCase()?.indexOf(a) >= 0
  );

  return (
    <ErrorBoundary
      fallback={
        (props?.fallbackBlock?.length ?? 0) > 0 ? (
          <ContentArea components={props.fallbackBlock} />
        ) : (
          <></>
        )
      }
      onError={(error, info) => console.error(error, info)}
    >
      <Suspense fallback={showFallback ? <ComponentLazyShimmer /> : <></>}>
        <SingleComponent {...props} contentType={contentType} />
      </Suspense>
    </ErrorBoundary>
  );
});

const ComponentFactory: React.FC<IComponentFactoryProps> = React.memo(
  ({ components, componentsProps }) => (
    <>
      {components?.map((component, index) => (
        <Component
          key={index}
          {...component}
          {...componentsProps}
          isFirstItem={index === 0}
          index={index}
          isLastItem={index === components.length - 1}
        />
      ))}
    </>
  )
);

export default ComponentFactory;
